import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Ideas from "@/pages/Ideas";
import Learn from "@/pages/Learn";
import Diagnostic from "@/pages/Diagnostic";
import DiagnosticResult from "@/pages/DiagnosticResult";
import Consulting from "@/pages/Consulting";
import Work from "@/pages/Work";
import WorkCase from "@/pages/WorkCase";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import CaseBottleneck from "@/pages/CaseBottleneck";
import PatternCase from "@/pages/PatternCase";
import Privacy from "@/pages/Privacy";
import CookieConsent, { trackPageView } from "@/components/CookieConsent";

function ScrollManager() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.hash);
    const lenis = window.__lenis;
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        const t = setTimeout(() => {
          if (lenis) lenis.scrollTo(el, { offset: -88, duration: 1.4 });
          else el.scrollIntoView();
        }, 60);
        return () => clearTimeout(t);
      }
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function NotFound() {
  return (
    <main className="wrap flex min-h-screen flex-col items-start justify-center pt-24" data-testid="not-found-page">
      <p className="kicker">404 — OFF THE MAP</p>
      <h1 className="mt-6 text-4xl font-semibold tracking-tight text-slate-100 sm:text-5xl">
        This node doesn't exist.
      </h1>
      <p className="mt-4 max-w-md text-base text-slate-400">
        The path you followed isn't part of the system. It happens in organisations too.
      </p>
      <Link to="/" className="btn-primary mt-10" data-testid="not-found-home-link">
        Back to the map
      </Link>
    </main>
  );
}

function App() {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App grain bg-ink text-slate-200">
      <BrowserRouter>
        <ScrollManager />
        <Nav />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/ideas" element={<Ideas />} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/diagnostic" element={<Diagnostic />} />
          <Route path="/diagnostic/result/:token" element={<DiagnosticResult />} />
          <Route path="/consulting" element={<Consulting />} />
          <Route path="/work" element={<Work />} />
          <Route path="/work/:slug" element={<WorkCase />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/patterns/manager-bottleneck" element={<CaseBottleneck />} />
          <Route path="/patterns/:slug" element={<PatternCase />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer />
        <CookieConsent />
      </BrowserRouter>
    </div>
  );
}

export default App;
